/**
 * Admin Billing Management page — fuel costs, charge rates, credits/debits, licence types.
 * Route: #/admin/billing
 * Requirements: 17.2, 17.3, 17.4, 17.5
 */

import { getUser, isAdmin } from '../services/auth.js';
import {
  getFuelCosts,
  setFuelCost,
  getBillingRates,
  createBillingRate,
  updateBillingRate,
  deleteBillingRate,
  getPilots,
  getAircraft,
  createAccountEntry,
  getLicenceAircraftMappings,
  upsertLicenceAircraftMapping,
  deleteLicenceAircraftMapping,
  getOutstandingInvoices,
  createPaymentAllocation,
  updateInvoice,
  getBillingRecords,
  getAllAccountEntries,
  getPilotBillingHistory,
} from '../services/api.js';
import { navigate } from '../services/router.js';
import { formatDateUK, formatGBP } from '../utils/format.js';
import { createDataGrid } from '../components/data-grid.js';
import { openDetailModal } from '../components/detail-modal.js';
import { computeRemainingBalance, validateAllocationAmount } from './billing.js';
import { createBillingLedger } from '../components/billing-ledger.js';
import { createCsvExportButton } from '../components/csv-export-button.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testability)
// ---------------------------------------------------------------------------

/**
 * Format a fuel cost record for display.
 *
 * @param {Object} fuelCost - { id, month, cost_per_litre }
 * @returns {{ id: string, month: string, costPerLitre: string }}
 */
export function formatFuelCostForDisplay(fuelCost) {
  if (!fuelCost) return { id: '', month: '—', costPerLitre: '—' };

  const monthStr = fuelCost.month
    ? new Date(fuelCost.month + 'T00:00:00').toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })
    : '—';

  const cost = fuelCost.cost_per_litre != null
    ? `£${Number(fuelCost.cost_per_litre).toFixed(4)}`
    : '—';

  return {
    id: fuelCost.id || '',
    month: monthStr,
    costPerLitre: cost,
  };
}

/**
 * Format an account entry for display.
 *
 * @param {Object} entry - { id, pilot_id, entry_type, amount, description, entry_date, pilotName? }
 * @returns {{ id: string, date: string, pilot: string, type: string, amount: string, description: string }}
 */
export function formatAccountEntry(entry) {
  if (!entry) return { id: '', date: '—', pilot: '—', type: '—', amount: '—', description: '—' };

  return {
    id: entry.id || '',
    date: entry.entry_date ? formatDateUK(entry.entry_date) : '—',
    pilot: entry.pilotName || entry.pilots?.name || '—',
    type: entry.entry_type || '—',
    amount: entry.amount != null ? formatGBP(Number(entry.amount)) : '—',
    description: entry.description || '—',
  };
}

/**
 * Prepare a fuel cost payload from form data.
 *
 * @param {Object} formData - { month: string, cost_per_litre: string }
 * @returns {{ month: string, costPerLitre: number }}
 */
export function prepareFuelCostPayload(formData) {
  const month = (formData.month || '').trim();
  // Normalise to first day of month if only YYYY-MM provided
  const normalisedMonth = month.length === 7 ? `${month}-01` : month;
  const costPerLitre = parseFloat(formData.cost_per_litre);

  return {
    month: normalisedMonth,
    costPerLitre: isNaN(costPerLitre) ? 0 : costPerLitre,
  };
}

/**
 * Prepare an account entry payload from form data.
 *
 * @param {Object} formData - { pilot_id, entry_type, amount, description, entry_date }
 * @returns {{ pilot_id: string, entry_type: string, amount: number, description: string, entry_date: string }}
 */
export function prepareAccountEntryPayload(formData) {
  return {
    pilot_id: (formData.pilot_id || '').trim(),
    entry_type: (formData.entry_type || 'CREDIT').trim(),
    amount: parseFloat(formData.amount) || 0,
    description: (formData.description || '').trim(),
    entry_date: (formData.entry_date || new Date().toISOString().slice(0, 10)).trim(),
  };
}

/**
 * Aggregate billing data per pilot for the admin summary table.
 *
 * @param {Object[]} billingRecords - All billing records (debits). Each has pilot_id, amount_due, pilots.name.
 * @param {Object[]} accountEntries - All pilot_account_entries (credits/debits). Each has pilot_id, entry_type, amount, pilots.name.
 * @param {Object[]} pilots - All pilot records. Each has id, name.
 * @returns {Array<{ pilotId: string, pilotName: string, totalDebits: number, totalCredits: number, outstandingBalance: number }>}
 *   Sorted by pilotName ascending.
 * Requirements: 13.1
 */
export function aggregatePilotBilling(billingRecords, accountEntries, pilots) {
  const map = new Map();

  // Seed with all pilots so everyone appears even with zero activity
  for (const p of (pilots || [])) {
    map.set(p.id, {
      pilotId: p.id,
      pilotName: p.name || '—',
      totalDebits: 0,
      totalCredits: 0,
      outstandingBalance: 0,
    });
  }

  // Sum debits from billing records (flights with amount_due)
  for (const r of (billingRecords || [])) {
    const pid = r.pilot_id;
    if (!pid) continue;
    if (!map.has(pid)) {
      map.set(pid, {
        pilotId: pid,
        pilotName: r.pilots?.name || '—',
        totalDebits: 0,
        totalCredits: 0,
        outstandingBalance: 0,
      });
    }
    const entry = map.get(pid);
    entry.totalDebits += Number(r.amount_due) || 0;
  }

  // Sum credits from account entries
  for (const ae of (accountEntries || [])) {
    const pid = ae.pilot_id;
    if (!pid) continue;
    if (!map.has(pid)) {
      map.set(pid, {
        pilotId: pid,
        pilotName: ae.pilots?.name || '—',
        totalDebits: 0,
        totalCredits: 0,
        outstandingBalance: 0,
      });
    }
    const entry = map.get(pid);
    const amount = Number(ae.amount) || 0;
    if (ae.entry_type === 'CREDIT') {
      entry.totalCredits += amount;
    } else if (ae.entry_type === 'DEBIT') {
      entry.totalDebits += amount;
    }
  }

  // Compute outstanding balance and round
  const result = [];
  for (const entry of map.values()) {
    entry.totalDebits = Math.round(entry.totalDebits * 100) / 100;
    entry.totalCredits = Math.round(entry.totalCredits * 100) / 100;
    entry.outstandingBalance = Math.round((entry.totalDebits - entry.totalCredits) * 100) / 100;
    result.push(entry);
  }

  // Sort by pilot name ascending
  result.sort((a, b) => (a.pilotName || '').localeCompare(b.pilotName || ''));
  return result;
}

/**
 * Generate a CSV string from pilot billing summary data.
 *
 * @param {Array<{ pilotName: string, totalDebits: number, totalCredits: number, outstandingBalance: number }>} summaryData
 * @returns {string} CSV string with header row and data rows
 * Requirements: 13.4
 */
export function generateBillingSummaryCSV(summaryData) {
  const header = 'Pilot Name,Total Debits,Total Credits,Outstanding Balance';
  const rows = (summaryData || []).map((s) => {
    const name = `"${(s.pilotName || '').replace(/"/g, '""')}"`;
    return `${name},${(s.totalDebits || 0).toFixed(2)},${(s.totalCredits || 0).toFixed(2)},${(s.outstandingBalance || 0).toFixed(2)}`;
  });
  return [header, ...rows].join('\n');
}

// ---------------------------------------------------------------------------
// Page render
// ---------------------------------------------------------------------------

/**
 * Render the admin billing management page.
 * Admin-only — redirects non-admin users.
 */
export async function renderAdminBilling() {
  const app = document.getElementById('app');
  if (!app) return;

  const user = getUser();
  if (!user) { navigate('/login'); return; }
  if (!isAdmin()) { navigate('/'); return; }


  app.innerHTML = `
    <div class="container">
      <h2>Billing Management</h2>
      <div id="admin-billing-msg"></div>

      <div class="card">
        <h3>Pilot Billing Summary</h3>
        <div id="pilot-billing-summary">Loading…</div>
        <div id="pilot-billing-detail" style="display:none;"></div>
      </div>

      <div class="card">
        <h3>Fuel Costs per Litre</h3>
        <div id="fuel-costs-grid">Loading…</div>
      </div>

      <div class="card">
        <h3>Aircraft Charge Rates</h3>
        <div id="charge-rates-grid">Loading…</div>
      </div>

      <div class="card">
        <h3>Credit / Debit Entry</h3>
        <div id="account-entry-grid">Loading…</div>
      </div>

      <div class="card">
        <h3>Licence Type Mappings</h3>
        <div id="licence-types-grid">Loading…</div>
      </div>
    </div>
  `;

  // Load reference data
  let fuelCosts = [];
  let billingRates = [];
  let pilots = [];
  let aircraft = [];
  let licenceMappings = [];
  let billingRecords = [];
  let accountEntries = [];

  try {
    [fuelCosts, billingRates, pilots, aircraft, licenceMappings, billingRecords, accountEntries] = await Promise.all([
      getFuelCosts(),
      getBillingRates(),
      getPilots(),
      getAircraft(),
      getLicenceAircraftMappings(),
      getBillingRecords(),
      getAllAccountEntries(),
    ]);
  } catch (err) {
    showMsg('Failed to load data: ' + err.message, true);
    return;
  }

  // ── Pilot Billing Summary Grid ──────────────────────────────────────────

  let showOutstandingOnly = false;

  function renderPilotBillingSummary() {
    const container = document.getElementById('pilot-billing-summary');
    container.innerHTML = '';

    const summaryData = aggregatePilotBilling(billingRecords, accountEntries, pilots);

    const filteredData = showOutstandingOnly
      ? summaryData.filter((s) => s.outstandingBalance > 0)
      : summaryData;

    const displayData = filteredData.map((s) => ({
      pilotId: s.pilotId,
      pilotName: s.pilotName,
      totalDebits: s.totalDebits,
      totalCredits: s.totalCredits,
      outstandingBalance: s.outstandingBalance,
    }));

    // Toolbar with filter checkbox and CSV export button
    const toolbar = document.createElement('div');
    toolbar.className = 'pilot-billing-toolbar';
    toolbar.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;';

    // Filter checkbox
    const filterLabel = document.createElement('label');
    filterLabel.style.cssText = 'display:flex;align-items:center;gap:0.5rem;cursor:pointer;';
    const filterCheckbox = document.createElement('input');
    filterCheckbox.type = 'checkbox';
    filterCheckbox.checked = showOutstandingOnly;
    filterCheckbox.addEventListener('change', () => {
      showOutstandingOnly = filterCheckbox.checked;
      renderPilotBillingSummary();
    });
    filterLabel.appendChild(filterCheckbox);
    filterLabel.appendChild(document.createTextNode('Show only pilots with outstanding balance'));
    toolbar.appendChild(filterLabel);

    // CSV export button
    const csvBtn = createCsvExportButton({
      label: 'Export CSV',
      getCsvContent: () => generateBillingSummaryCSV(displayData),
      filename: 'pilot-billing-summary.csv',
    });
    toolbar.appendChild(csvBtn);

    container.appendChild(toolbar);

    const grid = createDataGrid({
      columns: [
        { key: 'pilotName', label: 'Pilot Name' },
        { key: 'totalDebits', label: 'Total Debits', format: (v) => formatGBP(v) },
        { key: 'totalCredits', label: 'Total Credits', format: (v) => formatGBP(v) },
        {
          key: 'outstandingBalance',
          label: 'Outstanding Balance',
          format: (v) => formatGBP(v),
          html: true,
        },
      ],
      data: displayData,
      defaultSort: 'pilotName',
      emptyMessage: 'No pilot billing data',
      onRowClick: (row) => showPilotBillingDetail(row.pilotId, row.pilotName),
      rowClass: (row) => {
        if (row.outstandingBalance > 0) return 'data-grid__row--danger';
        if (row.outstandingBalance < 0) return 'data-grid__row--success';
        return null;
      },
    });

    container.appendChild(grid);
  }

  async function showPilotBillingDetail(pilotId, pilotName) {
    const summaryContainer = document.getElementById('pilot-billing-summary');
    const detailContainer = document.getElementById('pilot-billing-detail');

    // Hide summary, show detail
    summaryContainer.style.display = 'none';
    detailContainer.style.display = '';
    detailContainer.innerHTML = '';

    // Back button
    const backBtn = document.createElement('button');
    backBtn.className = 'btn btn--secondary';
    backBtn.textContent = '← Back to Summary';
    backBtn.style.marginBottom = '1rem';
    backBtn.addEventListener('click', () => {
      detailContainer.style.display = 'none';
      detailContainer.innerHTML = '';
      summaryContainer.style.display = '';
    });
    detailContainer.appendChild(backBtn);

    // Heading
    const heading = document.createElement('h4');
    heading.textContent = `Billing Ledger — ${pilotName}`;
    heading.style.marginBottom = '1rem';
    detailContainer.appendChild(heading);

    // Loading indicator
    const loadingEl = document.createElement('p');
    loadingEl.textContent = 'Loading billing history…';
    detailContainer.appendChild(loadingEl);

    try {
      const { billingRecords: debits, accountEntries: credits } = await getPilotBillingHistory(pilotId);
      loadingEl.remove();

      const ledger = createBillingLedger({ debits, credits, adminMode: true });
      detailContainer.appendChild(ledger);
    } catch (err) {
      loadingEl.textContent = `Error loading billing history: ${err.message}`;
    }
  }

  renderPilotBillingSummary();

  // ── Fuel Costs Grid ─────────────────────────────────────────────────────

  function renderFuelCostsGrid() {
    const container = document.getElementById('fuel-costs-grid');
    container.innerHTML = '';

    const displayData = fuelCosts.map(formatFuelCostForDisplay);

    const grid = createDataGrid({
      columns: [
        { key: 'month', label: 'Month' },
        { key: 'costPerLitre', label: 'Cost per Litre' },
      ],
      data: displayData,
      defaultSort: 'month',
      defaultOrder: 'desc',
      emptyMessage: 'No fuel costs configured',
      onAdd: () => openFuelCostModal(),
      addLabel: 'Set Fuel Cost',
      onRowClick: (row) => {
        const fc = fuelCosts.find((f) => f.id === row.id);
        if (fc) openFuelCostModal(fc);
      },
    });

    container.appendChild(grid);
  }

  function openFuelCostModal(existing = null) {
    const isEdit = !!existing;
    openDetailModal({
      title: isEdit ? 'Edit Fuel Cost' : 'Set Fuel Cost',
      fields: [
        { name: 'month', label: 'Month (YYYY-MM)', type: 'month', required: true },
        { name: 'cost_per_litre', label: 'Cost per Litre (£)', type: 'number', required: true },
      ],
      values: isEdit ? {
        month: existing.month ? existing.month.slice(0, 7) : '',
        cost_per_litre: existing.cost_per_litre != null ? String(existing.cost_per_litre) : '',
      } : null,
      onSave: async (formData) => {
        const { month, costPerLitre } = prepareFuelCostPayload(formData);
        await setFuelCost(month, costPerLitre);
        fuelCosts = await getFuelCosts();
        renderFuelCostsGrid();
        showMsg('Fuel cost saved.', false);
      },
    });
  }

  renderFuelCostsGrid();

  // ── Charge Rates Grid ───────────────────────────────────────────────────

  function renderChargeRatesGrid() {
    const container = document.getElementById('charge-rates-grid');
    container.innerHTML = '';

    const displayData = billingRates.map((r) => ({
      id: r.id,
      aircraft: r.aircraft?.registration || '—',
      ratePerHour: r.rate_per_hour != null ? formatGBP(Number(r.rate_per_hour)) : '—',
      basis: r.billing_basis || '—',
      effectiveFrom: r.effective_from ? formatDateUK(r.effective_from) : '—',
      effectiveTo: r.effective_to ? formatDateUK(r.effective_to) : 'Ongoing',
    }));

    const grid = createDataGrid({
      columns: [
        { key: 'aircraft', label: 'Aircraft' },
        { key: 'ratePerHour', label: 'Rate / Hour' },
        { key: 'basis', label: 'Billing Basis' },
        { key: 'effectiveFrom', label: 'From' },
        { key: 'effectiveTo', label: 'To' },
      ],
      data: displayData,
      defaultSort: 'effectiveFrom',
      defaultOrder: 'desc',
      emptyMessage: 'No charge rates configured',
      onAdd: () => openChargeRateModal(),
      addLabel: 'Add Rate',
      actions: [
        {
          label: 'Edit',
          className: 'btn--primary',
          onClick: (row) => {
            const rate = billingRates.find((r) => r.id === row.id);
            if (rate) openChargeRateModal(rate);
          },
        },
        {
          label: 'Delete',
          className: 'btn--danger',
          onClick: async (row) => {
            if (!confirm('Delete this rate?')) return;
            try {
              await deleteBillingRate(row.id);
              billingRates = await getBillingRates();
              renderChargeRatesGrid();
              showMsg('Rate deleted.', false);
            } catch (err) {
              showMsg('Delete failed: ' + err.message, true);
            }
          },
        },
      ],
    });

    container.appendChild(grid);
  }

  function openChargeRateModal(existing = null) {
    const isEdit = !!existing;
    const aircraftOptions = aircraft.map((a) => ({
      value: a.id,
      label: `${a.registration} (${a.type})`,
    }));

    openDetailModal({
      title: isEdit ? 'Edit Charge Rate' : 'Add Charge Rate',
      fields: [
        { name: 'aircraft_id', label: 'Aircraft', type: 'select', options: aircraftOptions, required: true },
        { name: 'rate_per_hour', label: 'Rate per Hour (£)', type: 'number', required: true },
        {
          name: 'billing_basis', label: 'Billing Basis', type: 'select',
          options: [
            { value: 'BLOCK_TIME', label: 'Block Time' },
            { value: 'FLIGHT_TIME', label: 'Flight Time' },
          ],
          required: true,
        },
        { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
        { name: 'effective_to', label: 'Effective To', type: 'date' },
      ],
      values: isEdit ? {
        aircraft_id: existing.aircraft_id || '',
        rate_per_hour: existing.rate_per_hour != null ? String(existing.rate_per_hour) : '',
        billing_basis: existing.billing_basis || 'BLOCK_TIME',
        effective_from: existing.effective_from || '',
        effective_to: existing.effective_to || '',
      } : null,
      onSave: async (formData) => {
        const payload = {
          aircraft_id: formData.aircraft_id,
          rate_per_hour: parseFloat(formData.rate_per_hour),
          billing_basis: formData.billing_basis,
          effective_from: formData.effective_from,
          effective_to: formData.effective_to || null,
        };
        if (isEdit) {
          await updateBillingRate(existing.id, payload);
        } else {
          await createBillingRate(payload);
        }
        billingRates = await getBillingRates();
        renderChargeRatesGrid();
        showMsg(isEdit ? 'Rate updated.' : 'Rate created.', false);
      },
      onDelete: isEdit ? async () => {
        await deleteBillingRate(existing.id);
        billingRates = await getBillingRates();
        renderChargeRatesGrid();
        showMsg('Rate deleted.', false);
      } : undefined,
    });
  }

  renderChargeRatesGrid();

  // ── Account Entry Section ───────────────────────────────────────────────

  function renderAccountEntrySection() {
    const container = document.getElementById('account-entry-grid');
    container.innerHTML = '';

    const pilotOptions = pilots.map((p) => ({
      value: p.id,
      label: p.name,
    }));

    const addBtn = document.createElement('button');
    addBtn.className = 'data-grid__add-btn';
    addBtn.textContent = 'Add Credit / Debit';
    addBtn.addEventListener('click', () => {
      openDetailModal({
        title: 'Add Credit / Debit Entry',
        fields: [
          { name: 'pilot_id', label: 'Pilot', type: 'select', options: pilotOptions, required: true },
          {
            name: 'entry_type', label: 'Type', type: 'select',
            options: [
              { value: 'CREDIT', label: 'Credit' },
              { value: 'DEBIT', label: 'Debit' },
            ],
            required: true,
          },
          { name: 'amount', label: 'Amount (£)', type: 'number', required: true },
          { name: 'description', label: 'Description', type: 'text', required: true },
          { name: 'entry_date', label: 'Date', type: 'date', required: true },
        ],
        values: {
          entry_date: new Date().toISOString().slice(0, 10),
          entry_type: 'CREDIT',
        },
        onSave: async (formData) => {
          const payload = prepareAccountEntryPayload(formData);
          const { pilot_id, ...entry } = payload;
          const createdEntry = await createAccountEntry(pilot_id, entry);
          showMsg('Account entry added.', false);

          // If this is a CREDIT entry, offer payment allocation
          if (payload.entry_type === 'CREDIT' && payload.amount > 0) {
            await openPaymentAllocationForm(pilot_id, payload.amount, createdEntry.id, pilots);
          }
        },
      });
    });

    container.appendChild(addBtn);

    const info = document.createElement('p');
    info.textContent = 'Use the button above to add a credit or debit to any pilot\'s account.';
    info.style.color = '#666';
    info.style.marginTop = '0.5rem';
    container.appendChild(info);
  }

  renderAccountEntrySection();

  // ── Licence Type Mappings Grid ──────────────────────────────────────────

  function renderLicenceMappingsGrid() {
    const container = document.getElementById('licence-types-grid');
    container.innerHTML = '';

    const displayData = licenceMappings.map((m) => ({
      id: m.id,
      ratingCategory: m.rating_category || '—',
      aircraftType: m.aircraft_type || '—',
    }));

    const grid = createDataGrid({
      columns: [
        { key: 'ratingCategory', label: 'Rating Category' },
        { key: 'aircraftType', label: 'Aircraft Type' },
      ],
      data: displayData,
      defaultSort: 'ratingCategory',
      emptyMessage: 'No licence type mappings configured',
      onAdd: () => openLicenceMappingModal(),
      addLabel: 'Add Mapping',
      actions: [
        {
          label: 'Edit',
          className: 'btn--primary',
          onClick: (row) => {
            const mapping = licenceMappings.find((m) => m.id === row.id);
            if (mapping) openLicenceMappingModal(mapping);
          },
        },
        {
          label: 'Delete',
          className: 'btn--danger',
          onClick: async (row) => {
            if (!confirm('Delete this mapping?')) return;
            try {
              await deleteLicenceAircraftMapping(row.id);
              licenceMappings = await getLicenceAircraftMappings();
              renderLicenceMappingsGrid();
              showMsg('Mapping deleted.', false);
            } catch (err) {
              showMsg('Delete failed: ' + err.message, true);
            }
          },
        },
      ],
    });

    container.appendChild(grid);
  }

  function openLicenceMappingModal(existing = null) {
    const isEdit = !!existing;
    openDetailModal({
      title: isEdit ? 'Edit Licence Mapping' : 'Add Licence Mapping',
      fields: [
        { name: 'rating_category', label: 'Rating Category', type: 'text', required: true },
        { name: 'aircraft_type', label: 'Aircraft Type', type: 'text', required: true },
      ],
      values: isEdit ? {
        rating_category: existing.rating_category || '',
        aircraft_type: existing.aircraft_type || '',
      } : null,
      onSave: async (formData) => {
        await upsertLicenceAircraftMapping({
          rating_category: formData.rating_category.trim(),
          aircraft_type: formData.aircraft_type.trim(),
        });
        licenceMappings = await getLicenceAircraftMappings();
        renderLicenceMappingsGrid();
        showMsg(isEdit ? 'Mapping updated.' : 'Mapping created.', false);
      },
      onDelete: isEdit ? async () => {
        await deleteLicenceAircraftMapping(existing.id);
        licenceMappings = await getLicenceAircraftMappings();
        renderLicenceMappingsGrid();
        showMsg('Mapping deleted.', false);
      } : undefined,
    });
  }

  renderLicenceMappingsGrid();

  // ── Payment Allocation Form ──────────────────────────────────────────────

  /**
   * Open a payment allocation form after creating a credit entry.
   * Shows outstanding invoices for the pilot and allows the admin to allocate
   * the credit amount across one or more invoices.
   *
   * @param {string} pilotId - The pilot's UUID
   * @param {number} creditAmount - The total credit entry amount
   * @param {string} accountEntryId - The created account entry UUID
   * @param {Object[]} allPilots - All pilots for name lookup
   */
  async function openPaymentAllocationForm(pilotId, creditAmount, accountEntryId, allPilots) {
    // Remove any existing allocation modal
    const existing = document.getElementById('admin-payment-allocation-modal');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'admin-payment-allocation-modal';
    overlay.style.cssText = `
      position:fixed;top:0;left:0;width:100%;height:100%;
      background:rgba(0,0,0,0.5);z-index:1100;
      display:flex;align-items:center;justify-content:center;
    `;
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });

    const modal = document.createElement('div');
    modal.style.cssText = `
      background:var(--color-bg, #fff);border-radius:0.75rem;
      padding:1.5rem;max-width:750px;width:90%;max-height:85vh;
      overflow-y:auto;box-shadow:0 4px 24px rgba(0,0,0,0.2);
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;';

    const title = document.createElement('h3');
    title.style.margin = '0';
    title.textContent = 'Allocate Payment to Invoices';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn btn--secondary';
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = 'font-size:1.25rem;padding:0.25rem 0.5rem;line-height:1;';
    closeBtn.addEventListener('click', () => overlay.remove());

    header.appendChild(title);
    header.appendChild(closeBtn);
    modal.appendChild(header);

    // Pilot name
    const pilot = allPilots.find((p) => p.id === pilotId);
    const pilotInfo = document.createElement('p');
    pilotInfo.style.cssText = 'margin-bottom:0.5rem;';
    pilotInfo.textContent = `Pilot: ${pilot?.name || 'Unknown'}`;
    modal.appendChild(pilotInfo);

    // Credit info
    const creditInfo = document.createElement('div');
    creditInfo.style.cssText = 'margin-bottom:0.75rem;font-size:1rem;';
    creditInfo.innerHTML = `<strong>Credit Amount:</strong> ${formatGBP(creditAmount)}`;
    modal.appendChild(creditInfo);

    // Remaining to allocate
    const remainingDisplay = document.createElement('div');
    remainingDisplay.id = 'admin-allocation-remaining';
    remainingDisplay.style.cssText = 'margin-bottom:1rem;font-size:0.95rem;font-weight:bold;';
    remainingDisplay.textContent = `Remaining to allocate: ${formatGBP(creditAmount)}`;
    modal.appendChild(remainingDisplay);

    // Loading
    const loadingEl = document.createElement('p');
    loadingEl.textContent = 'Loading outstanding invoices…';
    modal.appendChild(loadingEl);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Load outstanding invoices
    let invoices = [];
    try {
      invoices = await getOutstandingInvoices(pilotId);
    } catch (err) {
      loadingEl.textContent = `Error loading invoices: ${err.message}`;
      return;
    }

    loadingEl.remove();

    if (invoices.length === 0) {
      const noInvoices = document.createElement('p');
      noInvoices.style.color = 'var(--color-text-muted)';
      noInvoices.textContent = 'No outstanding invoices for this pilot.';
      modal.appendChild(noInvoices);

      const doneBtn = document.createElement('button');
      doneBtn.className = 'btn btn--secondary';
      doneBtn.textContent = 'Close';
      doneBtn.style.marginTop = '1rem';
      doneBtn.addEventListener('click', () => overlay.remove());
      modal.appendChild(doneBtn);
      return;
    }

    // Invoice allocation table
    const table = document.createElement('table');
    table.style.cssText = 'width:100%;border-collapse:collapse;margin-bottom:1rem;';

    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr>
        <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Period</th>
        <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Status</th>
        <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Total</th>
        <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Paid</th>
        <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Remaining</th>
        <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Allocate</th>
      </tr>
    `;
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    const allocationInputs = [];

    for (const inv of invoices) {
      const invRemaining = computeRemainingBalance(inv.total_amount, inv.paid_amount);
      if (invRemaining <= 0) continue;

      const tr = document.createElement('tr');
      tr.style.borderBottom = '1px solid var(--color-border, #eee)';

      const tdPeriod = document.createElement('td');
      tdPeriod.style.padding = '0.5rem';
      tdPeriod.textContent = `${formatDateUK(inv.period_start)} – ${formatDateUK(inv.period_end)}`;
      tr.appendChild(tdPeriod);

      const tdStatus = document.createElement('td');
      tdStatus.style.padding = '0.5rem';
      tdStatus.textContent = inv.status;
      tr.appendChild(tdStatus);

      const tdTotal = document.createElement('td');
      tdTotal.style.cssText = 'text-align:right;padding:0.5rem;';
      tdTotal.textContent = formatGBP(inv.total_amount);
      tr.appendChild(tdTotal);

      const tdPaid = document.createElement('td');
      tdPaid.style.cssText = 'text-align:right;padding:0.5rem;';
      tdPaid.textContent = formatGBP(inv.paid_amount || 0);
      tr.appendChild(tdPaid);

      const tdRemaining = document.createElement('td');
      tdRemaining.style.cssText = 'text-align:right;padding:0.5rem;';
      tdRemaining.textContent = formatGBP(invRemaining);
      tr.appendChild(tdRemaining);

      const tdAllocate = document.createElement('td');
      tdAllocate.style.cssText = 'text-align:right;padding:0.5rem;';
      const input = document.createElement('input');
      input.type = 'number';
      input.step = '0.01';
      input.min = '0';
      input.max = invRemaining.toFixed(2);
      input.value = '';
      input.placeholder = '0.00';
      input.style.cssText = 'width:100px;text-align:right;';
      input.dataset.invoiceId = inv.id;
      input.dataset.remaining = invRemaining;
      input.addEventListener('input', updateRemaining);
      tdAllocate.appendChild(input);
      tr.appendChild(tdAllocate);

      allocationInputs.push(input);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    modal.appendChild(table);

    // Error display
    const errorEl = document.createElement('p');
    errorEl.style.cssText = 'color:var(--color-danger, red);display:none;margin-bottom:0.5rem;';
    modal.appendChild(errorEl);

    // Buttons
    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:0.5rem;';

    const submitBtn = document.createElement('button');
    submitBtn.className = 'btn btn--primary';
    submitBtn.textContent = 'Apply Allocations';
    submitBtn.addEventListener('click', handleSubmit);

    const skipBtn = document.createElement('button');
    skipBtn.className = 'btn btn--secondary';
    skipBtn.textContent = 'Skip';
    skipBtn.addEventListener('click', () => overlay.remove());

    btnRow.appendChild(submitBtn);
    btnRow.appendChild(skipBtn);
    modal.appendChild(btnRow);

    function updateRemaining() {
      const totalAllocated = allocationInputs.reduce((sum, inp) => sum + (parseFloat(inp.value) || 0), 0);
      const remainingToAllocate = Math.max(0, creditAmount - totalAllocated);
      const el = document.getElementById('admin-allocation-remaining');
      if (el) {
        el.textContent = `Remaining to allocate: ${formatGBP(remainingToAllocate)}`;
        el.style.color = remainingToAllocate > 0 ? 'var(--color-warning, orange)' : 'var(--color-success, green)';
      }
    }

    async function handleSubmit() {
      errorEl.style.display = 'none';

      const allocations = [];
      let totalAllocated = 0;

      for (const input of allocationInputs) {
        const amt = parseFloat(input.value) || 0;
        if (amt <= 0) continue;

        const invoiceId = input.dataset.invoiceId;
        const invRemaining = parseFloat(input.dataset.remaining) || 0;

        const validation = validateAllocationAmount(amt, invRemaining, creditAmount, totalAllocated);
        if (!validation.valid) {
          errorEl.textContent = validation.error;
          errorEl.style.display = 'block';
          return;
        }

        allocations.push({ invoice_id: invoiceId, amount: amt });
        totalAllocated += amt;
      }

      if (allocations.length === 0) {
        overlay.remove();
        return;
      }

      if (totalAllocated > creditAmount + 0.005) {
        errorEl.textContent = `Total allocated (${formatGBP(totalAllocated)}) exceeds credit amount (${formatGBP(creditAmount)}).`;
        errorEl.style.display = 'block';
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = 'Applying…';

      try {
        for (const alloc of allocations) {
          await createPaymentAllocation({
            account_entry_id: accountEntryId,
            invoice_id: alloc.invoice_id,
            amount: alloc.amount,
          });

          // Update invoice paid_amount and status
          const inv = invoices.find((i) => i.id === alloc.invoice_id);
          if (inv) {
            const newPaidAmount = Math.round(((Number(inv.paid_amount) || 0) + alloc.amount) * 100) / 100;
            const totalAmount = Number(inv.total_amount) || 0;

            const updates = { paid_amount: newPaidAmount };

            if (newPaidAmount >= totalAmount) {
              updates.status = 'PAID';
              updates.paid_at = new Date().toISOString();
            } else if (inv.status === 'DRAFT') {
              updates.status = 'ISSUED';
              updates.issued_at = new Date().toISOString();
            }

            await updateInvoice(alloc.invoice_id, updates);
            inv.paid_amount = newPaidAmount;
          }
        }

        overlay.remove();
        showMsg('Payment allocations applied successfully.', false);
      } catch (err) {
        errorEl.textContent = `Error applying allocations: ${err.message}`;
        errorEl.style.display = 'block';
        submitBtn.disabled = false;
        submitBtn.textContent = 'Apply Allocations';
      }
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  function showMsg(text, isError) {
    const el = document.getElementById('admin-billing-msg');
    if (!el) return;
    el.textContent = text;
    el.className = isError ? 'msg msg--error' : 'msg msg--success';
    setTimeout(() => { el.textContent = ''; el.className = ''; }, 4000);
  }
}
